/**
 * Presentation formatters. The ERPat API returns machine values (ISO-8601
 * timestamps, numeric amounts); the UI renders human labels. Keep all such
 * formatting here so it stays consistent across pages.
 *
 * Every parser tolerates `null` / `undefined` / unparseable input and returns
 * an empty string (or a sensible default) rather than throwing — server field
 * shapes are not strictly typed, so defensiveness is the rule.
 */

/** A bare calendar date with no time part, e.g. `1995-03-12`. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse a value that may be an ISO string, epoch ms, or epoch seconds. */
export function toDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    // Heuristic: 10-digit values are seconds, 13-digit are milliseconds.
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' && value.trim()) {
    const text = value.trim();
    // `new Date('1995-03-12')` is spec'd to parse a date-only string as UTC
    // midnight, but every local getter (and toLocaleDateString) then reads it
    // back in the device's zone — so west of UTC it renders as the day before.
    // These fields are calendar dates, not instants: birthdays, hire dates,
    // leave/payroll periods, deadlines. Build them at LOCAL midnight so the day
    // is stable everywhere. This also matches how the backend's other format
    // ('2026-06-15 08:00:00', no zone) already parses. Timestamps that DO carry
    // a time — with or without an offset — keep their existing handling below.
    if (DATE_ONLY_RE.test(text)) {
      const year = Number(text.slice(0, 4));
      const month = Number(text.slice(5, 7));
      const day = Number(text.slice(8, 10));
      const local = new Date(year, month - 1, day);
      // Impossible dates ('2026-02-31') roll over instead of failing; the ISO
      // parser rejected them, so keep returning null rather than a silent shift.
      if (local.getFullYear() !== year || local.getMonth() !== month - 1 || local.getDate() !== day) {
        return null;
      }
      return local;
    }
    const d = new Date(text);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Epoch milliseconds for sorting, or 0 when unparseable. */
export function toMillis(value: unknown): number {
  return toDate(value)?.getTime() ?? 0;
}

/** e.g. "9:00 AM". */
export function formatTime(value: unknown): string {
  const d = toDate(value);
  return d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
}

/** e.g. "May 20, 2025". */
export function formatDate(value: unknown): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '';
}

/** e.g. "Mon", "Tue". */
export function formatWeekday(value: unknown): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString('en-US', { weekday: 'short' }) : '';
}

/**
 * Local calendar date as `YYYY-MM-DD` — the LOCAL day, unlike `toISOString()`
 * which renders the UTC day (and can be off by one near midnight). Returns ''
 * for unparseable input. Used to key/compare attendance punches by their day.
 */
export function localDateKey(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The seven dates (Sunday-first) of the week containing `ref` (default: today). */
export function currentWeekDates(ref: Date = new Date()): Date[] {
  const base = new Date(ref);
  base.setHours(0, 0, 0, 0);
  const sunday = new Date(base);
  sunday.setDate(base.getDate() - base.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return d;
  });
}

/**
 * Every local calendar date from `startKey` to `endKey` inclusive, both
 * `YYYY-MM-DD`. Built by stepping a local Date so DST transitions can't drop or
 * duplicate a day the way adding 24h in milliseconds would. Returns [] when
 * either bound is unparseable or the range runs backwards, and is capped at
 * `maxDays` so a malformed bound can't spin out a huge array.
 */
export function datesInRange(startKey: string, endKey: string, maxDays = 62): Date[] {
  const start = toDate(startKey);
  const end = toDate(endKey);
  if (!start || !end) return [];
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start > end) return [];

  const out: Date[] = [];
  const cursor = new Date(start);
  while (cursor <= end && out.length < maxDays) {
    out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/** Relative label like "2h ago", "Yesterday", or a date for older items. */
export function formatRelative(value: unknown): string {
  const d = toDate(value);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  if (sec < 60) return 'Just now';
  if (min < 60) return `${min}m ago`;
  if (hr < 24) return `${hr}h ago`;
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day}d ago`;
  return formatDate(d);
}

/* ── Chat day labels ─────────────────────────────────────────────────────
 *
 * A message thread needs a DIFFERENT notion of "recent" than a list row.
 * `formatRelative` measures ELAPSED time, so a message sent at 11:50 PM reads
 * "20m ago" when you open the thread at 12:10 AM — true, but it hides that the
 * message belongs to YESTERDAY, which is the one thing a day separator exists
 * to say. These helpers compare LOCAL CALENDAR DAYS instead.
 */

/** Nominal ms in a day — DST's 23h/25h days are absorbed by rounding below. */
const DAY_MS = 86_400_000;

/**
 * `toDate`, except the number `0` means ABSENT rather than the Unix epoch.
 *
 * Every message mapper fills its timestamp with `toMillis`, which reports `0`
 * when the server sent no usable value — and "Jan 1, 1970" is never a day
 * label anyone wants to read. (The assistant page already guards `message.at`
 * the same way before formatting.)
 */
function toStampDate(value: unknown): Date | null {
  return value === 0 ? null : toDate(value);
}

/**
 * Whole LOCAL calendar days from `value` back to `ref`: `0` same day, `1` the
 * day before, `-1` the day after.
 *
 * Measured between local MIDNIGHTS, never between the instants themselves, so
 * two timestamps 20 minutes apart across midnight are correctly one day apart.
 * `Math.round` keeps DST transition days (23h / 25h) on whole-day boundaries.
 */
function localDaysBetween(value: Date, ref: Date): number {
  const from = new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const to = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime();
  return Math.round((to - from) / DAY_MS);
}

/**
 * The relative CALENDAR-day caption for a chat day separator:
 * "Today", "Yesterday", "Tomorrow", a weekday inside the past week
 * ("Monday"), else a date — "Sep 12", or "Sep 12, 2025" in another year.
 *
 * Returns '' when there is no usable timestamp, so a caller can skip the
 * separator entirely rather than print a placeholder.
 *
 * @param refMs "now" as epoch ms — pass {@link useToday}'s value so the label
 *   re-renders when the date rolls over under a thread left open overnight.
 */
export function formatDayLabel(value: unknown, refMs: number = Date.now()): string {
  const d = toStampDate(value);
  if (!d) return '';
  const days = localDaysBetween(d, new Date(refMs));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days === -1) return 'Tomorrow';
  // Within the past week the weekday reads faster than a date, and is
  // unambiguous (a date one week out would repeat the same weekday).
  if (days > 1 && days < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  // Older, or further into the future. The year only earns its space when it
  // is not the current one.
  return d.getFullYear() === new Date(refMs).getFullYear()
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Relative day + clock for one message, e.g. "Today · 4:44 PM",
 * "Yesterday · 3:05 AM", "Sep 12 · 2:18 PM".
 *
 * A bare clock ("4:44 PM") is ambiguous the moment a thread spans midnight —
 * which is exactly what a work chat does. Returns '' when the timestamp is
 * unusable; callers fall back to whatever their mapper produced.
 */
export function formatDayTime(value: unknown, refMs: number = Date.now()): string {
  const day = formatDayLabel(value, refMs);
  if (!day) return '';
  const time = formatTime(value);
  return time ? `${day} · ${time}` : day;
}

/** Currency label, e.g. "₱4,000.00". Defaults to PHP. */
export function formatMoney(amount: unknown, currency = 'PHP'): string {
  const n = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency }).format(n);
  } catch {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

/** Avatar initials from a display name, e.g. "Angela Martin" → "AM". */
export function initialsOf(name: unknown, max = 2): string {
  if (typeof name !== 'string' || !name.trim()) return '';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, max)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/** "08:32" elapsed label from a minutes count → "8h 32m". */
export function formatDurationMinutes(totalMinutes: unknown): string {
  const n = typeof totalMinutes === 'number' ? totalMinutes : Number(totalMinutes);
  if (!Number.isFinite(n) || n < 0) return '0h 00m';
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

/**
 * A running shift timer — always `H:MM:SS` ("0:07:42", "8:05:10"), so a shift
 * under an hour still reads as a shift clock. Fractions of a second are
 * dropped; a negative or unreadable value reads `0:00:00`.
 *
 * Not `formatElapsed` (views/projects): that one drops the hour under an hour,
 * which is right for a task stopwatch and wrong for time worked today.
 */
export function formatStopwatch(totalSeconds: number): string {
  const s = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** A count to at most two decimals, without trailing zeros: 4 → "4", 0.125 → "0.13". */
function compactCount(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** A leave's hours, e.g. "1 hour", "4 hours", "3.04 hours". */
export function formatLeaveHours(hours: number): string {
  return `${compactCount(hours)} ${hours === 1 ? 'hour' : 'hours'}`;
}

/**
 * A share of a leave day, e.g. "0.5 day", "1 day", "2 days" — plural only above
 * one, as ERPat's web leave list writes it.
 */
export function formatLeaveDays(days: number): string {
  return `${compactCount(days)} ${days > 1 ? 'days' : 'day'}`;
}

/**
 * A leave's stored total as the ERPat web leave dialog writes it, e.g.
 * "0.50 Day (4.00 Hours)" — "Day"/"Hour" unless the figure is above one
 * (`Leaves::_make_row()`); the hours are left off when the server sent none.
 */
export function formatLeaveTotal(days: number, hours: number): string {
  const total = `${days.toFixed(2)} ${days > 1 ? 'Days' : 'Day'}`;
  return hours > 0 ? `${total} (${hours.toFixed(2)} ${hours > 1 ? 'Hours' : 'Hour'})` : total;
}

/** A payroll cut-off window: an inclusive local-date span plus its label. */
export interface CutoffPeriod {
  /** Inclusive first day, `YYYY-MM-DD` — the attendance API's `start_date`. */
  startKey: string;
  /** Inclusive last day, `YYYY-MM-DD` — the attendance API's `end_date`. */
  endKey: string;
  /** e.g. "Sep 01 – Sep 15". Year-less: the span is at most 16 days. */
  label: string;
}

/**
 * "Sep 1 – Sep 15, 2026" — the full, year-bearing cut-off span, as the Timesheet
 * cut-off browser titles the period it is showing.
 *
 * The year rides on the END only, unless the span crosses a year boundary (a
 * 26–10 calendar does, every December) in which case both halves carry one.
 *
 * Both keys are parsed as LOCAL MIDDAY, never `new Date('YYYY-MM-DD')` — that
 * parses as UTC and renders as the previous day for anyone west of Greenwich,
 * silently shifting every label back by one.
 */
export function cutoffRangeLabel(startKey: string, endKey: string): string {
  const at = (key: string): Date | null => {
    const [y, m, d] = key.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12);
  };
  const start = at(startKey);
  const end = at(endKey);
  if (!start || !end) return '';

  const startOpts: Intl.DateTimeFormatOptions =
    start.getFullYear() === end.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' };
  return `${start.toLocaleDateString('en-US', startOpts)} – ${end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

/**
 * "Sep 11 - 25, 2026" — a narrower Home-widget cut-off span.
 *
 * Same-month periods omit the repeated end month so the compact App Dash cards
 * keep the range on one line. Cross-month periods keep both month names
 * ("Aug 26 - Sep 10, 2026"), and cross-year periods keep both years.
 */
export function compactCutoffRangeLabel(startKey: string, endKey: string): string {
  const at = (key: string): Date | null => {
    const [y, m, d] = key.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12);
  };
  const start = at(startKey);
  const end = at(endKey);
  if (!start || !end) return '';

  if (start.getFullYear() !== end.getFullYear()) {
    return `${start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
  }

  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

/** "Sep 01" — the compact half of a cut-off label. */
function monthDay(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
}

/**
 * The semi-monthly payroll cut-off `offset` periods back from the one holding
 * `ref` (`0` = the period in progress, `-1` = the one before it).
 *
 * Semi-monthly means the 1st–15th and the 16th–end of month, which is the shape
 * ERPat payslips carry (`payroll_basis: "semi_monthly"`, `period_start` /
 * `period_end`). Month length is read from the calendar, so February and the
 * 30-day months end on their own last day rather than a hard-coded 31.
 *
 * DERIVED ON THE DEVICE, not fetched: the end-user API exposes cut-off
 * boundaries only on issued payslips (`/me/payslips`), which say nothing about
 * the period still in progress — and nothing at all to a manager looking at
 * someone else's attendance.
 */
export function semiMonthlyCutoff(offset = 0, ref: Date = new Date()): CutoffPeriod {
  // Index the half-months on one continuous axis so stepping back across a
  // month — or a year — boundary needs no special case.
  const index = ref.getFullYear() * 24 + ref.getMonth() * 2 + (ref.getDate() <= 15 ? 0 : 1) + offset;
  const year = Math.floor(index / 24);
  const month = Math.floor((index % 24) / 2);
  const firstHalf = index % 2 === 0;
  const start = new Date(year, month, firstHalf ? 1 : 16);
  // Day 0 of the next month is the last day of this one.
  const end = firstHalf ? new Date(year, month, 15) : new Date(year, month + 1, 0);
  return {
    startKey: localDateKey(start),
    endKey: localDateKey(end),
    label: `${monthDay(start)} – ${monthDay(end)}`,
  };
}
